import { useState, useEffect, useCallback } from 'react';
import { useErrorHandler } from '../../utils/useErrorHandler';
import { Transaction, CreditAdjustment } from '../types';
import dataProvider from '../../services/api';
import { TRANSACTION_MANAGEMENT_CONSTANTS } from '../constants/transactionManagement';

export const useTransactions = (userId?: number) => {
  const { handleErrorWithLogout, showSuccessMessage } = useErrorHandler();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [transactionsPerPage, setTransactionsPerPage] = useState<number>(TRANSACTION_MANAGEMENT_CONSTANTS.DEFAULT_TRANSACTIONS_PER_PAGE);

  const handleTransactionsPerPageChange = useCallback((newTransactionsPerPage: number) => {
    if (newTransactionsPerPage !== transactionsPerPage) {
      setTransactionsPerPage(newTransactionsPerPage);
      setCurrentPage(1); 
    }
  }, [transactionsPerPage]);

  const fetchTransactions = useCallback(async () => {
    
    try {
      if (!userId) {
        throw new Error('User ID is required to fetch transactions');
      }
      const { data } = await dataProvider.getUserTransactions({
        userId,
        filter: searchTerm,
        page: currentPage - 1,
        perPage: transactionsPerPage,
      });
      setTransactions(data.transactions || []);
      setTotalTransactions(data.total || 0);
    } catch (error: any) {
      handleErrorWithLogout(error);
      setTransactions([]);
      setTotalTransactions(0);
    }
  }, [userId, searchTerm, currentPage, transactionsPerPage]);

  const adjustCredits = useCallback(async (adjustment: CreditAdjustment) => {
    if (!userId) return false;
    try {
      await dataProvider.adjustUserCredits({
        userId,
        amount: adjustment.amount,
        transactionType: adjustment.type,
        reason: adjustment.description,
      });
      showSuccessMessage('Credit adjustment successful');
      await fetchTransactions();
      return true;
    } catch (error: any) {
      handleErrorWithLogout(error);
      return false;
    }
  }, [userId]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, Math.ceil(totalTransactions / transactionsPerPage))));
  }, [totalTransactions, transactionsPerPage]);

  useEffect(() => {
    if (userId) {
      setCurrentPage(1);
      fetchTransactions();
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchTransactions();
    }
  }, [currentPage, searchTerm, transactionsPerPage]); 

  return {
    transactions,
    searchTerm,
    setSearchTerm,
    currentPage,
    totalTransactions,
    transactionsPerPage,
    handlePageChange,
    adjustCredits,
    fetchTransactions,
    onTransactionsPerPageChange: handleTransactionsPerPageChange, 
  };
};